import React from 'react';
import { View } from 'react-native';
import { ListItem, Toggle } from '../../components';
import Text from '../../components/text/Text';
import { Tag } from '../../design/components';
import { DEFAULT_VENDOUT_THRESHOLDS } from '../../../common/constants';
import { formatCurrency } from '../../../common/utils/utils';
import { addressToString } from '../../../common/utils/address';
import { getFullMachineName } from '../../../common/utils/machine';
import isListColumnVisible from '../../components/list/isListColumnVisible';
import { useUserContext } from '../../../common/utils';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const ServiceRunFormMachineListItem = ({
	item: machine,
	customProps,
}) => {
	const { userSettings } = useUserContext();
	const styles = useThemedStyles(createStyles);

	const {
		toggleMachine,
		isMachineSelected,
		tenantSettings,
	} = customProps;

	// Tenant settings stored without vendoutThresholds arrive as null; fall back to the default
	const [vendoutWarningThreshold, vendoutCriticalThreshold] = Array.isArray(tenantSettings?.vendoutThresholds)
		? tenantSettings.vendoutThresholds
		: DEFAULT_VENDOUT_THRESHOLDS;

	const address = machine.location ? addressToString(machine.location.addressObj) : null;

	const getVendoutTone = value => {
		if (value >= vendoutCriticalThreshold) {
			return 'danger';
		}
		if (value >= vendoutWarningThreshold) {
			return 'warning';
		}
		return 'neutral';
	};

	return (
		<ListItem style={styles.item}>
			<View style={styles.row}>
				<View style={styles.body}>
					<Text style={styles.machineName}>
						{ getFullMachineName(machine, machine.place, machine.location) }
					</Text>
					{ address ? <Text style={styles.machineAttribute}>{address}</Text> : null}
					<View style={styles.tagRow}>
						{
							isListColumnVisible(userSettings, 'sr_detail_machine_ms', { name: 'salesRestock' }) && (
								<Tag label={`$ SOLD: ${formatCurrency(machine.salesSinceLastRestock)}`} />
							)
						}
						<Tag
							label={`VO: ${machine.vendouts === null ? '-' : machine.vendouts}`}
							tone={getVendoutTone(machine.vendouts)}
						/>
						<Tag
							label={`VOP: ${machine.vendoutPredictionTomorrow === null ? '-' : machine.vendoutPredictionTomorrow}`}
							tone={getVendoutTone(machine.vendoutPredictionTomorrow)}
						/>
						{
							machine.isOffline && (
								<Tag label="OFFLINE" />
							)
						}
					</View>
					<View style={styles.attributeRow}>
						{
							machine.assetId ? (
								<Text style={styles.machineAttribute}>
									{machine.assetId}
								</Text>
							)
								: null
						}

						{
							machine.machineModel ? (
								<Text style={styles.machineAttribute}>
									{machine.machineModel.name}
								</Text>
							)
								: null
						}
					</View>
				</View>
				<Toggle
					onChange={() => toggleMachine(machine)}
					enabled={isMachineSelected(machine)}
					key={machine.id}
				/>
			</View>
		</ListItem>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	item: {
		marginHorizontal: space.gutter,
		marginTop: 0,
		marginBottom: space.sm,
		paddingHorizontal: 14,
		paddingVertical: space.md,
	},
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
	},
	body: {
		flex: 1,
		gap: 6,
	},
	tagRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.xs,
	},
	attributeRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		columnGap: space.sm,
	},
	machineName: {
		...type.rowTitleSemibold,
		color: colors.text,
	},
	machineAttribute: {
		...type.secondary,
		color: colors.textSecondary,
	},
});

export default ServiceRunFormMachineListItem;
